/**
 * The language provider and the EN / ID switch (see lib/i18n.ts).
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { currentLocale, I18nContext, LS_KEY, translate, useI18n, type I18nValue, type Locale } from '@/lib/i18n';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(currentLocale);
  const setLocale = useCallback((l: Locale) => {
    try { localStorage.setItem(LS_KEY, l); } catch { /* noop */ }
    document.documentElement.lang = l;
    setLocaleState(l);
  }, []);
  const value = useMemo<I18nValue>(() => ({
    locale,
    setLocale,
    t: (key, params) => translate(locale, key, params),
  }), [locale, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** EN / ID switch, used on the sign-in screen and in the menu. */
export function LanguageSwitch({ className = '' }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <div className={`inline-flex rounded-md border border-border p-0.5 text-xs ${className}`} role="group" aria-label={t('lang.label')}>
      {(['en', 'id'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLocale(l)}
          aria-pressed={locale === l}
          className={`min-h-[32px] px-2.5 rounded font-semibold transition-colors ${locale === l ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          title={l === 'id' ? 'Bahasa Indonesia' : 'English'}
        >
          {l === 'id' ? 'ID' : 'EN'}
        </button>
      ))}
    </div>
  );
}
